import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button, Card } from '@ludolio/shared-ui';
import { CreateProjectDialog } from '@/components/CreateProjectDialog';
import { NO_LABS_UNITS_MESSAGE, hasNoLabsUnits } from '@/lib/units';
import { Cover, EmptyState, PageHeader, ReleaseStatusBadge } from '@/components/common';
import { getErrorMessage, labsApi } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { useLabs } from '@/lib/labs-context';
import type { LabProjectSummary } from '@/lib/types';

export const Route = createFileRoute('/$orgSlug/projects/')({
  component: ProjectsPage,
});

function ProjectsPage() {
  const labs = useLabs();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<LabProjectSummary[] | null>(null);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    labsApi
      .listProjects(labs.organization.slug)
      .then(setProjects)
      .catch((err) => setError(getErrorMessage(err)));
  }, [labs.organization.slug]);

  const canCreate = !hasNoLabsUnits(labs);
  const newProjectButton = canCreate ? (
    <Button onClick={() => setCreating(true)}>
      <Plus />
      New project
    </Button>
  ) : undefined;

  return (
    <>
      <PageHeader title="Projects" actions={projects && projects.length > 0 ? newProjectButton : undefined} />

      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      {!projects && !error && <p className="text-sm text-muted-foreground">Loading…</p>}
      {projects?.length === 0 && (
        <EmptyState title={canCreate ? 'You have no projects yet' : NO_LABS_UNITS_MESSAGE} action={newProjectButton} />
      )}
      {!canCreate && projects && projects.length > 0 && (
        <p className="mb-4 text-sm text-muted-foreground">{NO_LABS_UNITS_MESSAGE}</p>
      )}

      {projects && projects.length > 0 && (
        <ul className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <li key={project.id}>
              <ProjectCard project={project} orgSlug={labs.organization.slug} />
            </li>
          ))}
        </ul>
      )}

      <CreateProjectDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(project) => {
          setCreating(false);
          navigate({
            to: '/$orgSlug/projects/$projectId',
            params: { orgSlug: labs.organization.slug, projectId: project.project.id },
          });
        }}
      />
    </>
  );
}

function ProjectCard({ project, orgSlug }: { project: LabProjectSummary; orgSlug: string }) {
  const takenDown = !project.publishedVersion && !project.openStatus && project.isTakenDown;
  const status = takenDown ? 'withdrawn' : project.openStatus ?? (project.publishedVersion ? 'published' : 'draft');

  return (
    <Link
      to="/$orgSlug/projects/$projectId"
      params={{ orgSlug, projectId: project.id }}
      className="block h-full rounded-lg outline-[hsl(var(--color-ring))] focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2"
    >
      <Card className="flex h-full flex-col overflow-hidden transition-shadow hover:shadow-md">
        <Cover src={project.bannerUrl} title={project.title} />
        <div className="flex flex-1 flex-col gap-3 p-4">
          <div className="flex items-start justify-between gap-3">
            <h2 className="line-clamp-1 font-semibold">{project.title}</h2>
            <ReleaseStatusBadge status={status} />
          </div>
          <p className="line-clamp-1 min-h-5 text-sm text-muted-foreground">
            {project.team.name}{project.unit ? ` | ${project.unit.code}` : ''}
          </p>
          <p className="line-clamp-1 min-h-5 text-xs text-muted-foreground">
            {project.parent
              ? `Remix of ${project.parent.title}${project.parent.withdrawn ? ' (taken down)' : ''}`
              : ''}
          </p>
          <div className="mt-auto flex items-center justify-between text-xs text-muted-foreground">
            <span>{project.publishedVersion ? `Live ${project.publishedVersion}` : 'Not published'}</span>
            <span>{formatDate(project.updatedAt)}</span>
          </div>
        </div>
      </Card>
    </Link>
  );
}
